/**
 * Keeps blocklist details readable while limiting edits to owners and the
 * administrators responsible for the blocklist.
 */
import React, { useMemo, useState, useEffect } from 'react';
import { useParams } from 'react-router';
import { useForm, FormProvider } from 'react-hook-form';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Tag from '@amzn/meridian/tag';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import { useTranslation } from 'src/arb-wrapper';
import { Loader } from 'src/components/common/Loader';
import { FormInput, FormSharedUserInput, FormGtiInput } from 'src/components/common/forms';
import { TablePagination } from 'src/components/common/data-table-action-bar/TablePagination';
import type { CampaignDesiredStateDto } from 'src/store/generated/leela-api-generated';
import { useCampaignConfigurationControllerGetQuery, useCatalogControllerGetCatalogTitlesMutation, useCampaignConfigurationControllerUpdateMutation, useCampaignsControllerFindManyQuery, type GtiBlocklistConfigValue } from 'src/store/leela-api';
import { AlertType } from 'src/components/common/alerts/alert.types';
import { useToasterContext } from 'src/components/common/alerts/ToasterContext';
import { useUser } from 'src/contexts/UserContext';
import { useGtiValidation } from 'src/hooks/useGtiValidation';
import { useMyPermissions } from 'src/hooks/useMyPermissions';
import { ConfigurationType, CreationSource } from 'src/constants';
import { parseGtisFromField } from 'src/utils';

export const BlocklistDetail: React.FC = () => {
  const { t } = useTranslation();
  const { createToast } = useToasterContext();
  const { id } = useParams<{ id: string }>();
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('titles');
  const [campaignsPagination, setCampaignsPagination] = useState({
    page: 1,
    pageSize: 10,
    pageCount: 1,
  });
  const [titlesPagination, setTitlesPagination] = useState({
    page: 1,
    pageSize: 10,
    pageCount: 1,
  });

  const { data: config, isLoading } = useCampaignConfigurationControllerGetQuery({ id: id ?? '' });
  const { user } = useUser();
  const { businessUnitAdminOf } = useMyPermissions();
  const configValue = config?.config as GtiBlocklistConfigValue | undefined;
  const isOwner = config?.owner !== undefined && config.owner === user?.username;
  const isBusinessUnitAdmin = config?.businessUnitId !== undefined &&
    businessUnitAdminOf.some(businessUnit => businessUnit.id === config.businessUnitId);
  const canEdit = user?.isAdmin === true || isOwner || isBusinessUnitAdmin;
  const [updateConfig, { isLoading: isUpdating }] = useCampaignConfigurationControllerUpdateMutation();

  const methods = useForm<{
    name: string;
    userSharedList: string[];
    gtis: string;
  }>({
    defaultValues: {
      name: '',
      userSharedList: [],
      gtis: '',
    },
    mode: 'onChange',
  });

  const { control, reset, formState, watch } = methods;

  const watchedGtis = watch('gtis');

  const [fetchTitles, { data: titles, isLoading: isFetchingTitles }] = useCatalogControllerGetCatalogTitlesMutation();

  const gtiTitleMap = useMemo(() => {
    if (!titles) return new Map();
    return new Map(
      titles.map(title => [
        title.gti ?? '',
        { title: title.title, coverImageUrl: title.coverImageUrl ?? undefined },
      ])
    );
  }, [titles]);

  // Use GTI validation hook
  const { parseValidGtiList } = useGtiValidation({
    value: watchedGtis || '',
    gtiTitleMap,
  });

  useEffect(() => {
    if (config && !isEditing) {
      reset({
        name: config.name,
        userSharedList: config.userSharedList ?? [],
        gtis: configValue?.gtis?.join('\n') ?? '',
      });
    }
  }, [config, reset, isEditing]);

  const { data: campaignsData } = useCampaignsControllerFindManyQuery({
    filters: { configId: id },
    page: campaignsPagination.page,
    pageSize: campaignsPagination.pageSize,
  });

  useEffect(() => {
    if (campaignsData?.meta?.pagination) {
      setCampaignsPagination(prevState => ({
        ...prevState,
        page: campaignsData.meta.pagination.page ?? 1,
        pageCount: campaignsData.meta.pagination.pageCount ?? 1,
      }));
    }
  }, [campaignsData?.meta?.pagination]);

  useEffect(() => {
    if (configValue?.gtis) {
      const totalTitles = configValue?.gtis.length;
      setTitlesPagination(prevState => ({
        ...prevState,
        pageCount: Math.ceil(totalTitles / prevState.pageSize),
      }));
    }
  }, [configValue?.gtis]);

  useEffect(() => {
    if (watchedGtis) {
      const gtiList = parseValidGtiList(watchedGtis);

      if (gtiList.length > 0) {
        void fetchTitles({
          getCatalogGtiRequest: {
            gtiList,
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedGtis, fetchTitles]);

  if (isLoading) {
    return (
      <Column alignmentHorizontal="center" alignmentVertical="center" spacing="400">
        <Loader isLoading>Loading...</Loader>
      </Column>
    );
  }

  if (!config) {
    return (
      <Column spacing="400">
        <Text>Blocklist not found</Text>
      </Column>
    );
  }

  const handleSave = async () => {
    if (isFetchingTitles) return;

    const formData = methods.getValues();
    try {
      const gtiList = parseGtisFromField(formData['gtis_valid' as keyof typeof formData] as string ?? '');

      await updateConfig({
        id: id!,
        updateCampaignConfigurationDto: {
          name: formData.name,
          userSharedList: formData.userSharedList && formData.userSharedList.length > 0
            ? formData.userSharedList
            : undefined,
          config: {
            configType: ConfigurationType.GTI_BLOCKLIST,
            gtis: gtiList,
          },
        },
      }).unwrap();

      createToast({
        type: AlertType.SUCCESS,
        message: t('blocklist_update_success'),
      });
      setIsEditing(false);
    } catch (error) {
      const err = error as { data?: { message?: string } };
      const errorMessage = err?.data?.message ?? t('blocklist_update_error');
      createToast({
        type: AlertType.ERROR,
        message: errorMessage,
      });
    }
  };

  const handleCancel = () => {
    reset({
      name: config?.name ?? '',
      userSharedList: config?.userSharedList ?? [],
      gtis: configValue?.gtis?.join('\n') ?? '',
    });
    setIsEditing(false);
  };

  return (
    <FormProvider {...methods}>
      <Column backgroundColor="default" spacing="400">
      <Row alignmentHorizontal="justify" alignmentVertical="center">
        <Heading level={1}>{t('blocklist_heading', { name: config.name })}</Heading>
        {isEditing
          ? (
          <Row spacing="200">
            <Button type="secondary" onClick={handleCancel}>
              {t('form_global_cancel')}
            </Button>
            <Button
              disabled={!formState.isValid || isUpdating}
              type="primary"
              onClick={handleSave}
            >
              {t('form_global_save')}
            </Button>
          </Row>
            )
          : (
          <Button
            data-testid="blocklist-detail-edit-button"
            disabled={!canEdit}
            type="secondary"
            onClick={() => { setIsEditing(true); }}
          >
            Edit
          </Button>
            )}
      </Row>

      <Box backgroundColor="default" spacingInset="none 400 400 none">
        <Column spacing="400">
          <Heading level={3}>{t('program_header_general_settings')}</Heading>

          {isEditing
            ? (
          <>
          <Row spacing="600">
            <Column width="fill">
              <FormInput
                // @ts-expect-error - Control type mismatch with react-hook-form
                control={control}
                label={t('form_global_name')}
                name="name"
                placeholder="Enter blocklist name"
                required
              />
            </Column>
          </Row>

          <FormSharedUserInput
            // @ts-expect-error - Control type mismatch with react-hook-form
            control={control}
            name="userSharedList"
          />
          </>
              )
            : (
          <Column spacing="300">
            <Row alignmentVertical="center" spacing="600">
              <Row spacing="200" width="fill">
                <Text type="b300">{t('form_global_name')}:</Text>
                <Text color="secondary">{config.name}</Text>
              </Row>
            </Row>
            <Row spacing="200">
              <Text type="b300">{t('program_shared_with_header')}:</Text>
              <Text color="secondary">{config.userSharedList?.length ? config.userSharedList.join(', ') : 'None'}</Text>
            </Row>
          </Column>
              )}
        </Column>
      </Box>

      <Divider />

      {!isEditing && (
        <TabGroup value={activeTab} onChange={setActiveTab}>
          <Tab value="titles">
            {`${t('form_campaign_titles_label')} (${configValue?.gtis?.length ?? 0})`}
          </Tab>
          <Tab value="campaigns">
            {`Associated Campaigns (${config.source === CreationSource.ADMIN ? 'All' : (campaignsData?.data?.length ?? 0)})`}
          </Tab>
        </TabGroup>
      )}

      {(!isEditing && activeTab === 'titles') || isEditing
        ? (
        <Box backgroundColor="default" spacingInset="400">
          <Column spacing="400">
          {isEditing
            ? (
          <FormGtiInput
            // @ts-expect-error - Control type mismatch with react-hook-form
            control={control}
            gtiTitleMap={gtiTitleMap}
            isFetching={isFetchingTitles}
            label={t('form_campaign_titles_label')}
            name="gtis"
            requireCoverImage={false}
            requireMinimum={false}
            required
            showInvalidFilteredWarning
          />
              )
            : configValue?.gtis && configValue?.gtis.length > 0
              ? (
              <Table headerRows={1} showDividers>
                <TableRow>
                  <TableCell alignmentHorizontal="center">{t('form_global_thumbnail')}</TableCell>
                  <TableCell>{t('form_global_title')}</TableCell>
                  <TableCell>{t('form_global_gti')}</TableCell>
                </TableRow>
                {configValue?.gtis
                  .slice((titlesPagination.page - 1) * titlesPagination.pageSize, titlesPagination.page * titlesPagination.pageSize)
                  .map((gti: string) => (
                  <TableRow key={gti}>
                    <TableCell alignmentHorizontal="center">
                      {gtiTitleMap?.has(gti) && gtiTitleMap.get(gti)?.coverImageUrl
                        ? (
                          <img
                            alt={gtiTitleMap.get(gti)?.title ?? gti}
                            src={gtiTitleMap.get(gti)?.coverImageUrl ?? ''}
                            style={{ maxWidth: '100px' }}
                            title={gtiTitleMap.get(gti)?.title ?? gti}
                          />
                          )
                        : null}
                    </TableCell>
                    <TableCell>
                      <Text color="primary" type="b200">
                        {gtiTitleMap?.get(gti)?.title ?? '-'}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <Text color="secondary" type="b100">
                        {gti}
                      </Text>
                    </TableCell>
                    </TableRow>
                  ))}
                </Table>
                )
              : (
              <Text color="secondary" type="b200">No titles in blocklist</Text>
                )}
          {!isEditing && configValue?.gtis && configValue?.gtis.length > 0 && (
            <TablePagination
              currentPage={titlesPagination.page}
              itemsPerPage={titlesPagination.pageSize}
              pageCount={titlesPagination.pageCount}
              onItemsPerPageChange={pageSize => {
                setTitlesPagination(prevState => ({
                  ...prevState,
                  page: 1,
                  pageSize,
                }));
              }}
              onPageChange={newPage => {
                setTitlesPagination(prevState => ({
                  ...prevState,
                  page: newPage,
                }));
              }}
            />
          )}
          </Column>
        </Box>
          )
        : null}

      {!isEditing && activeTab === 'campaigns' && (
        <Box backgroundColor="default" spacingInset="400">
          <Column spacing="400">
        {config.source === CreationSource.ADMIN
          ? (
          <Text color="secondary" type="b200">
            This global blocklist applies to all campaigns
          </Text>
            )
          : campaignsData?.data && campaignsData.data.length > 0
            ? (
          <Table headerRows={1} showDividers>
            <TableRow>
              <TableCell>{t('form_global_id')}</TableCell>
              <TableCell>{t('form_campaigns_publisher')}</TableCell>
              <TableCell></TableCell>
              <TableCell>{t('form_global_status')}</TableCell>
            </TableRow>
            {campaignsData.data.map((campaign: CampaignDesiredStateDto) => (
              <TableRow
                highlightOnHover
                key={campaign.campaignId}
                onClick={() => { window.open(`/campaigns/${campaign.campaignId}`, '_blank'); }}
              >
                <TableCell>{campaign.campaignId}</TableCell>
                <TableCell>{campaign.publisher}</TableCell>
                <TableCell>
                  {campaign.productStrategies?.map((strategy, index) => (
                    <Row key={index} spacingInset="none none 300 none">
                      <Column spacing="300" spacingInset="200 100">
                        <Text type="h100">
                          {strategy.gtiList?.length ? t('custom_gti_list') : (strategy.strategyId ?? strategy.pageId)}
                        </Text>
                        {campaign.publisher?.toLowerCase() === 'adsp'
                          ? (
                            <Text color="secondary" type="b200">
                              {strategy.productSetName
                                ? `${t('form_campaigns_productSet_Name')}: ${strategy.productSetName}`
                                : strategy.productSetId
                                  ? `${t('form_campaigns_product_set')}: ${strategy.productSetId}`
                                  : null
                              }
                            </Text>
                            )
                          : (
                            <Text color="secondary" type="b200">
                              {t('form_campaigns_product_set')}: {strategy.productSetId}
                            </Text>
                            )}
                        <Text color="secondary" type="b200">
                          {t('form_country')}: {strategy.country} &middot; {t('form_locales')}: {strategy.locales.join(', ')}
                        </Text>
                      </Column>
                    </Row>
                  ))}
                </TableCell>
                <TableCell>
                  <Tag type={campaign.status === 'ACTIVE' ? 'success' : 'warning'}>{campaign.status}</Tag>
                </TableCell>
              </TableRow>
            ))}
          </Table>
              )
            : (
          <Text color="secondary" type="b200">
            No campaigns associated with this blocklist
          </Text>
              )}
        {campaignsData?.data && campaignsData.data.length > 0 && config.source !== CreationSource.ADMIN && (
          <TablePagination
            currentPage={campaignsPagination.page}
            itemsPerPage={campaignsPagination.pageSize}
            pageCount={campaignsPagination.pageCount}
            onItemsPerPageChange={pageSize => {
              setCampaignsPagination(prevState => ({
                ...prevState,
                page: 1,
                pageSize,
              }));
            }}
            onPageChange={newPage => {
              setCampaignsPagination(prevState => ({
                ...prevState,
                page: newPage,
              }));
            }}
          />
        )}
          </Column>
        </Box>
      )}
    </Column>
    </FormProvider>
  );
};
